import Model from "./Model";

export default class PropertyList extends Model {
    /**
     * Create a new property list
     * @param {Object} propertyList - Property list data object containing:
     *   @param {Number} propertyList.company_id - (Required) The ID of the company
     *   @param {String} propertyList.name - (Required) Name of the property list
     *   @param {String} propertyList.properties_list - (Required) JSON string array of property UUIDs
     * @returns {Promise<Object>} Created property list with id, uuid, and all fields
     * 
     * @example
     * const newList = await PropertyList.create({
     *   company_id: 1,
     *   name: "Main Campus Properties",
     *   properties_list: JSON.stringify(["property-uuid-1", "property-uuid-2", "property-uuid-3"])
     * });
     */
    static create = async(propertyList) => {
        const res = await this.runMutation(CreatePropertyList, {input: propertyList});
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ PropertyList.create.runMutation [COMPLETE]: ${JSON.stringify(res)}`);
        return res.data.createPropertyList;
    }

    /**
     * Update an existing property list
     * @param {Object} fields - Object containing fields to update:
     *   @param {Number} [fields.id] - Property list ID (required if uuid not provided)
     *   @param {String} [fields.uuid] - Property list UUID (required if id not provided)
     *   @param {String} [fields.name] - (Optional) New name for the property list
     *   @param {String} [fields.properties_list] - (Optional) New JSON string array of property UUIDs
     *   @param {Number} [fields.company_id] - (Optional) New company ID
     * @returns {Promise<Object>} Updated property list with all fields
     * 
     * @example
     * // Update by ID
     * const updated = await PropertyList.update({
     *   id: 1,
     *   name: "Updated Property Group Name",
     *   properties_list: JSON.stringify(["uuid-1", "uuid-2", "uuid-4", "uuid-5"])
     * });
     * 
     * @example
     * // Update by UUID
     * const updated = await PropertyList.update({
     *   uuid: "abc-123-def",
     *   name: "New Name Only"
     * });
     */
    static update = async(fields) => {
        if (!fields.id && !fields.uuid) {
            console.log("Can't update property list - no id or uuid specified");
            return;
        }
        const res = await this.runQueryNetworkOnly(UpdatePropertyList, {input: fields});
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ PropertyList.update.runQuery [COMPLETE]`);
        return res.data.updatePropertyList;
    }

    /**
     * Delete a property list by ID or UUID
     * @param {Number} [id] - Property list ID (optional if uuid provided)
     * @param {String} [uuid] - Property list UUID (optional if id provided)
     * @returns {Promise<Object>} Deleted property list containing id and uuid
     * 
     * @example
     * // Delete by ID
     * await PropertyList.delete(1);
     * 
     * @example
     * // Delete by UUID
     * await PropertyList.delete(null, "abc-123-def");
     */
    static delete = async(id, uuid) => {
        const input = {};
        if (id) input.id = id;
        if (uuid) input.uuid = uuid;
        
        if (!id && !uuid) {
            console.log("Can't delete property list - no id or uuid specified");
            return;
        }
        
        const res = await this.runMutation(DeletePropertyList, {input});
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ PropertyList.delete.runMutation [COMPLETE]`);
        return res.data.deletePropertyList;
    }

    /**
     * Get a single property list by ID or UUID
     * @param {Number} [id] - Property list ID (optional if uuid provided)
     * @param {String} [uuid] - Property list UUID (optional if id provided)
     * @returns {Promise<Object>} Property list object with all fields
     * 
     * @example
     * // Get by ID
     * const list = await PropertyList.getPropertyList(1);
     * 
     * @example
     * // Get by UUID
     * const list = await PropertyList.getPropertyList(null, "abc-123-def");
     */
    static getPropertyList = async(id, uuid) => {
        const vars = {};
        if (id) vars.id = id;
        if (uuid) vars.uuid = uuid;
        
        const res = await this.runQueryNetworkOnly(GetPropertyList, vars);
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ PropertyList.getPropertyList.runQuery [COMPLETE]`);
        return res.data.getPropertyList;
    }

    /**
     * Get all property lists for a specific company
     * @param {Number} companyId - The company ID to filter by
     * @returns {Promise<Array>} Array of property list objects
     * 
     * @example
     * const companyLists = await PropertyList.getPropertyListsByCompany(1);
     * // Returns: [{id: 1, uuid: "...", name: "...", properties_list: "...", ...}, ...]
     */
    static getPropertyListsByCompany = async(companyId) => {
        const res = await this.runQueryNetworkOnly(PropertyListsQuery, {company_id: companyId});
        console.log(`@@@@@@@@@@@@@@@@@@@@@@@@@ PropertyList.getPropertyListsByCompany.runQuery [COMPLETE]`);
        return res.data.propertyLists || [];
    }

    /**
     * Filter property lists with pagination
     * @param {Array} searchList - Search criteria
     * @param {String} sortField - Field to sort by
     * @param {String} sortOrder - Sort order (asc/desc)
     * @param {Number} first - Number of items to fetch
     * @param {String} after - Cursor for pagination
     * @param {Number} last - Number of items from end
     * @param {String} before - Cursor for reverse pagination
     * @returns {Promise<Object>} Filtered results with pagination info
     */
    static filterBy = async (searchList=[], sortField="created_at", sortOrder="desc", first, after, last, before) => {
        const input = {
            searchList,
            sortField,
            sortOrder
        };
        
        if (first) input.first = first;
        if (after) input.after = after;
        if (last) input.last = last;
        if (before) input.before = before;
        
        const res = await this.runQueryNetworkOnly(PropertyListFilter, {input});
        const result = res.data && res.data.propertyListFilter;
        return {
            data: result && (result.edges || []).map(e => e.node),
            pageInfo: result && result.pageInfo
        };
    }

    /**
     * Search property lists by name (partial match)
     * @param {String} name - Name to search for (supports partial matching)
     * @param {Number} [companyId] - Optional company ID to narrow search
     * @returns {Promise<Array>} Array of matching property list objects
     * 
     * @example
     * // Search all property lists containing "Campus"
     * const results = await PropertyList.searchByName("Campus");
     * 
     * @example
     * // Search within a specific company
     * const results = await PropertyList.searchByName("Main", 1);
     */
    static searchByName = async(name, companyId) => {
        const searchList = [
            {field: "name", value: name, comparison: "LIKE"}
        ];
        
        if (companyId) {
            searchList.push({field: "company_id", value: companyId, comparison: "="});
        }
        
        const results = await this.filterBy(searchList, "name", "asc", 100);
        return results.data || [];
    }

    /**
     * Get all property lists (limited to first 100)
     * @returns {Promise<Array>} Array of property lists
     */
    static all = async() => {
        const results = await this.filterBy([], "name", "asc", 100);
        return results.data || [];
    }

    /**
     * Get all property lists for a company (alias for getPropertyListsByCompany)
     * @param {Number} companyId - The company ID to filter by
     * @returns {Promise<Array>} Array of property list objects
     * 
     * @example
     * const lists = await PropertyList.getAll(1);
     */
    static getAll = async(companyId) => {
        return this.getPropertyListsByCompany(companyId);
    }
}

// GraphQL Mutations
const CreatePropertyList = `
mutation CreatePropertyList($input: PropertyListInput!) {
    createPropertyList(input: $input) {
        id
        uuid
        company_id
        created_at
        updated_at
        name
        properties_list
    }
}`;

const UpdatePropertyList = `
mutation UpdatePropertyList($input: PropertyListInput!) {
    updatePropertyList(input: $input) {
        id
        uuid
        company_id
        created_at
        updated_at
        name
        properties_list
    }
}`;

const DeletePropertyList = `
mutation DeletePropertyList($input: DeletePropertyListInput!) {
    deletePropertyList(input: $input) {
        id
        uuid
    }
}`;

// GraphQL Queries
const GetPropertyList = `
query GetPropertyList($id: Int, $uuid: ID) {
    getPropertyList(id: $id, uuid: $uuid) {
        id
        uuid
        company_id
        created_at
        updated_at
        name
        properties_list
    }
}`;

const PropertyListsQuery = `
query PropertyLists($company_id: Int) {
    propertyLists(company_id: $company_id) {
        id
        uuid
        company_id
        created_at
        updated_at
        name
        properties_list
    }
}`;

const PropertyListFilter = `
query PropertyListFilter($input: FilterConnectionInput) {
    propertyListFilter(input: $input) {
        pageInfo {
            endCursor
            startCursor
            hasNextPage
            hasPrevPage
            pageCount
            totalCount
            startIndex
            endIndex
        }
        edges {
            node {
                id
                uuid
                company_id
                created_at
                updated_at
                name
                properties_list
            }
        }
    }
}`;